/** Data-fetching wrapper around OpenPositionCard.
 *
 * Fetches today's trades filtered to OPEN. Passes them to the card
 * component. Auto-refreshes every 5s so trail steps and P&L moves become
 * visible without operator intervention. Also owns a ticking clock so the
 * "held" display in the card updates without the card having its own
 * timer. */

import { useEffect, useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { brokerApi } from "@/lib/brokerApi";
import { INSTRUMENTS } from "@/lib/instrument";
import { underlyingOf } from "@/lib/openPosition";
import { tradeLogApi } from "@/lib/tradeLogApi";
import { ALL_STRATEGIES, type TradeLogEntry } from "@/types/tradeLog";
import { playAlarm, showDesktopNotification, showHardNotification } from "@/lib/notify";
import { EmptyOpenPosition, OpenPositionCard } from "./OpenPositionCard";
import { UpdatedAt } from "./UpdatedAt";

const REFETCH_MS = 5_000;
const CLOCK_MS = 15_000;
// Live option LTP poll. Faster than the 5s trade refetch — this is THE
// number the operator watches mid-trade. One request per open trade;
// single-user + typically one position keeps Kite rate limits happy.
const LTP_REFETCH_MS = 3_000;

interface Props {
  /** When provided, restricts to trades on this strategy. Omit on the
   * Dashboard (aggregate across all strategies). */
  strategyId?: string;
  /** When true and rendering on Dashboard, add per-card "Open strategy →"
   * links so the operator can jump into details. */
  showStrategyLink?: boolean;
}

export function OpenPositionSection({
  strategyId,
  showStrategyLink = false,
}: Readonly<Props>): ReactElement {
  const now = useTickingClock();
  const q = useQuery({
    queryKey: ["open-positions", strategyId ?? "all"] as const,
    queryFn: async () => {
      // The backend segregates live from paper (execution_mode defaults to
      // LIVE and they never mix), but an OPEN position is an open position
      // to the operator — fetch both and merge, the card badges paper.
      const base = {
        range: "today",
        strategyId: strategyId ?? ALL_STRATEGIES,
        show: "trades",
      } as const;
      const [live, paper] = await Promise.all([
        tradeLogApi.list({ ...base }),
        tradeLogApi.list({ ...base, executionMode: "PAPER" }),
      ]);
      // Positional baskets render through BasketTile surfaces (Carried
      // baskets / StrategyBasketSection) — this card's SL/trail fields
      // are meaningless for a multi-leg basket.
      return [...live.trades, ...paper.trades].filter(
        (t) => t.exitReason === "OPEN" && t.horizon !== "POSITIONAL",
      );
    },
    refetchInterval: REFETCH_MS,
    // Keep the live card fresh while the operator is on another window
    // (React Query pauses interval refetch when unfocused by default).
    refetchIntervalInBackground: true,
  });
  const forceExit = useForceExit();

  const trades: TradeLogEntry[] = q.data ?? [];

  if (q.isPending) {
    return <section className="card p-4 text-sm text-ink-secondary">Loading position…</section>;
  }
  if (trades.length === 0) return <EmptyOpenPosition />;

  return (
    <div className="space-y-3">
      {trades.map((t) => (
        <LiveTradeCard
          key={t.id}
          trade={t}
          now={now}
          showStrategyLink={showStrategyLink}
          onForceExit={async (id) => {
            await forceExit.mutateAsync(id);
          }}
        />
      ))}
      <UpdatedAt updatedAtMs={q.dataUpdatedAt} now={now} />
    </div>
  );
}

/** Force-exit mutation with its cache invalidation (success) and hard
 * emergency alarm (failure) split out to keep OpenPositionSection lean. A
 * failed force-exit likely leaves the position OPEN at the broker, so it
 * pages the operator with a sticky notification + siren. */
function useForceExit(): ReturnType<typeof useMutation<TradeLogEntry, Error, string>> {
  const queryClient = useQueryClient();
  return useMutation<TradeLogEntry, Error, string>({
    mutationFn: (tradeId: string) => tradeLogApi.forceExit(tradeId),
    onSuccess: (trade) => {
      // Card renders PENDING_EXIT → operator sees the transition
      // immediately. Fill poller finishes the state machine within a
      // few seconds; the 5s refetch surfaces the CLOSED state.
      void queryClient.invalidateQueries({ queryKey: ["open-positions"] });
      void queryClient.invalidateQueries({ queryKey: ["trade-log"] });
      // ...unless the exchange was shut, in which case NOTHING was sent and
      // the position is still open. Say so at the moment of the click rather
      // than letting the operator infer it from a card that did not change
      // (operator, asked twice on 2026-08-18).
      if (trade.queuedExitAt !== null) {
        showDesktopNotification(
          "⏳ Exit queued, NOT sent",
          "The market is closed. This exit will be placed at the next open — the position is still open until then.",
          "nss-force-exit-queued",
        );
      }
    },
    onError: () => {
      showHardNotification(
        "🚨 FORCE EXIT FAILED",
        "The exit did NOT go through — the position may still be OPEN at the broker. Flatten it manually NOW.",
        "nss-force-exit-failed",
      );
      playAlarm();
    },
  });
}

/** Card + its own live-LTP poll. Kept as a child component so each open
 * trade gets an independent query and OpenPositionCard itself stays
 * presentational (renders fine in tests without a QueryClient). */
function LiveTradeCard({
  trade,
  now,
  showStrategyLink,
  onForceExit,
}: Readonly<{
  trade: TradeLogEntry;
  now: Date;
  showStrategyLink: boolean;
  onForceExit: (tradeId: string) => Promise<void>;
}>): ReactElement {
  const ltpQ = useQuery({
    queryKey: ["option-ltp", trade.contractSymbol] as const,
    queryFn: () => brokerApi.ltp(optionLtpSymbol(trade.contractSymbol ?? "")),
    enabled: trade.contractSymbol !== null,
    refetchInterval: LTP_REFETCH_MS,
  });
  // The INDEX beside the premium (operator, 2026-09-29). Resolved from the
  // trade's OWN contract rather than threaded down from the page: POI-1
  // renders no MarketStatusRow (it picks its own index per day, see
  // StrategyDetailPage), and the Dashboard shows several strategies at once,
  // so a page-level spot would be wrong or missing for exactly those cards.
  const underlying = underlyingOf(trade.contractSymbol);
  const spotSymbol = underlying === null ? null : INSTRUMENTS[underlying].symbol;
  const spotQ = useQuery({
    queryKey: ["index-spot", underlying] as const,
    queryFn: () => brokerApi.ltp(spotSymbol ?? ""),
    enabled: spotSymbol !== null,
    refetchInterval: LTP_REFETCH_MS,
    retry: false,
  });
  return (
    <OpenPositionCard
      trade={trade}
      now={now}
      showStrategyLink={showStrategyLink}
      onForceExit={onForceExit}
      liveLtp={ltpQ.data?.last_price ?? null}
      liveLtpStale={ltpQ.isError}
      liveSpot={spotQ.data?.last_price ?? null}
    />
  );
}

/** Kite LTP wants "EXCHANGE:TRADINGSYMBOL"; derive the F&O exchange from
 * the symbol prefix (SENSEX trades on BFO, NIFTY on NFO). */
function optionLtpSymbol(contractSymbol: string): string {
  const exchange = contractSymbol.startsWith("SENSEX") ? "BFO" : "NFO";
  return `${exchange}:${contractSymbol}`;
}

function useTickingClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => {
      setNow(new Date());
    }, CLOCK_MS);
    return () => {
      clearInterval(id);
    };
  }, []);
  return now;
}
